import _difference from "lodash/difference";
import * as React from 'react';
import { createPortal } from 'react-dom';
import nullthrows from "../lib/nullthrows";
import AlignContext, { inertAlignContextProps } from "../Align/AlignContext";
import FocusTrap from "../FocusTrap";
import { ClassicLayerCompat, ClassicLayerSailNextContext } from '@sail/global-state';
import { NextLayer } from "./NextLayer";
/**
 * The top-level DOM node containing all Portal mount targets.
 */
// @ts-expect-error - TS7034 - Variable 'layerRoot' implicitly has type 'any' in some locations where its type cannot be determined.
let layerRoot = null;

/**
 * A stack of Layer instances, for use with handling events in proper Layer
 * ancestry order.
 */
let layerStack = [];
const body = () => nullthrows(document.body);
const handleClick = e => {
  const {
    target
  } = e;
  if (!(target instanceof Node)) {
    return;
  }

  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  const ancestors = getAncestorLayersForNode(layerStack, target);
  const nonAncestors = _difference(layerStack, ancestors);
  for (let ii = nonAncestors.length - 1; ii >= 0; ii--) {
    const layer = nonAncestors[ii];
    const node = layer.getContainerDOMNode();
    if (node && !node.contains(target)) {
      const result = layer.onClickOutside(e);
      if (result === 'handled') {
        return;
      }
    }
  }
};
const handleScroll = e => {
  layerStack.forEach(layer => {
    layer.onScroll(e);
  });
};
const handleResizeWindow = e => {
  layerStack.forEach(layer => {
    layer.onResizeWindow(e);
  });
};
const addBodyListeners = () => {
  body().addEventListener('click', handleClick, true);
  window.addEventListener('scroll', handleScroll, true);
  window.addEventListener('resize', handleResizeWindow, false);
};
const removeBodyListeners = () => {
  body().removeEventListener('click', handleClick, true);
  window.removeEventListener('scroll', handleScroll, true);
  window.removeEventListener('resize', handleResizeWindow, false);
};
const LayerContext = /*#__PURE__*/React.createContext(null);
class Layer extends React.Component {
  constructor(props) {
    super(props);
    this.layer = void 0;
    this.targetNode = void 0;
    this.node = void 0;
    this.parentLayer = void 0;
    this.layerRef = node => {
      this.node = node;
    };
    /**
     * As of React 16, first-class portals bubble events up to parent components.
     * This can lead to unexpected behavior, especially where parent components
     * have event listeners that trigger layers to open or close. For instance, a
     * click on an element might open a flyout, but a click on a "Close" button
     * within that flyout could bubble up to the parent element, causing it to stay
     * open.
     *
     * It's easier to think of portals as being the top level for all event
     * bubbling for events occurring within the portal, since that's conceptually
     * similar to expected DOM behavior. To that end, we stop propagation on
     * bubbling events here.
     *
     * The list of handlers defined in `render` is based on
     * https://en.wikipedia.org/wiki/DOM_events#Events. It is not exhaustive since
     * callsites don't generally use the full set of events, but it aims to
     * cover some of the more common and obvious potential problem cases.
     */
    this.handleStop = e => {
      e.stopPropagation();
    };
    this.targetNode = document.createElement('div');
    // @ts-expect-error - TS7005 - Variable 'layerRoot' implicitly has an 'any' type.
    if (!layerRoot) {
      const node = document.createElement('div');
      body().appendChild(node);
      addBodyListeners();
      layerRoot = node;
    }
    layerRoot.appendChild(this.targetNode);

    // Add this Layer instance to the stack even if it has not yet rendered.
    layerStack = layerStack.concat(this);
  }
  componentWillUnmount() {
    // At this point, `layerRoot` should always be an element. This is in place
    // for Flow refinement.
    // @ts-expect-error - TS7005 - Variable 'layerRoot' implicitly has an 'any' type.
    if (!layerRoot) {
      return;
    }
    const index = layerStack.indexOf(this);
    const newStack = layerStack.slice();
    if (index !== -1) {
      newStack.splice(index, 1);
    }
    layerStack = newStack;

    // We're done with this portal mount target, so we can remove it from the
    // DOM.
    layerRoot.removeChild(this.targetNode);

    // If all children have been removed, we have no more layers and can
    // unhook everything.
    if (layerRoot.childNodes.length === 0) {
      body().removeChild(layerRoot);
      removeBodyListeners();
      layerRoot = null;
    }
  }
  /**
   * These methods are only for use within the static event handler
   * functions within Layer code. Do not use externally!
   */
  getParentLayer() {
    return this.parentLayer;
  }
  getContainerDOMNode() {
    return this.node;
  }
  onClickOutside(e) {
    const {
      onClickOutside
    } = this.props;
    if (onClickOutside) {
      return onClickOutside(e);
    }
    return 'not-handled';
  }
  onScroll(e) {
    const {
      onScroll
    } = this.props;
    if (onScroll) {
      onScroll(e);
    }
  }
  onResizeWindow(e) {
    const {
      onResizeWindow
    } = this.props;
    if (onResizeWindow) {
      onResizeWindow(e);
    }
  }
  render() {
    return /*#__PURE__*/React.createElement(ClassicLayerSailNextContext.Consumer, null, useNewLayers => /*#__PURE__*/React.createElement(LayerContext.Consumer, null, parentLayer => {
      this.parentLayer = parentLayer;
      // Checks that layer has content and is trapFocus is explicitly true
      const hasFocusTrap = this.props.trapFocus && !!this.node;
      return (
        /*#__PURE__*/
        // @ts-expect-error - TS2322 - Type 'this' is not assignable to type 'null'.
        React.createElement(LayerContext.Provider, {
          value: this
        }, /*#__PURE__*/React.createElement(AlignContext.Provider, {
          value: inertAlignContextProps
        }, useNewLayers ? /*#__PURE__*/React.createElement(NextLayer, {
          ref: this.layerRef,
          name: this.props.name
        }, /*#__PURE__*/React.createElement(FocusTrap, {
          className: this.props.className,
          trapFocus: hasFocusTrap,
          returnFocus: this.props.returnFocus,
          focusContainer: this.props.focusContainer
        }, React.Children.only(this.props.children))) : /*#__PURE__*/createPortal( /*#__PURE__*/React.createElement(ClassicLayerCompat, {
          onMouseDown: this.handleStop,
          onMouseUp: this.handleStop,
          onChange: this.handleStop,
          onClick: this.handleStop,
          onKeyDown: this.handleStop,
          onKeyPress: this.handleStop,
          onKeyUp: this.handleStop,
          onSubmit: this.handleStop,
          ref: this.layerRef
        }, /*#__PURE__*/React.createElement(FocusTrap, {
          className: this.props.className,
          trapFocus: hasFocusTrap,
          returnFocus: this.props.returnFocus,
          focusContainer: this.props.focusContainer
        }, React.Children.only(this.props.children))), this.targetNode)))
      );
    }));
  }
}
Layer.defaultProps = {
  returnFocus: false,
  trapFocus: false
};
export function getAncestorLayersForNode(layerStack, target) {
  const stack = layerStack.slice();
  const targetLayer = stack.reverse().find(layer => {
    const node = layer.getContainerDOMNode();
    return !!(node && node.contains(target));
  });
  if (!targetLayer) {
    return [];
  }
  const ancestors = [];
  let layerToCheck = targetLayer;
  while (layerToCheck) {
    ancestors.push(layerToCheck);
    // @ts-expect-error - TS2322 - Type 'Layer | null | undefined' is not assignable to type 'Layer'.
    layerToCheck = layerToCheck.getParentLayer();
  }
  return ancestors;
}
export default Layer;